/**
 * Private interface between the Platform Backend and the Robot Communication
 * Service ("comms"). Never exposed to clients or robots.
 *
 * Transport: the backend connects to comms over a WebSocket on a private port
 * (ws://comms:7001/internal) presenting `Authorization: Bearer <INTERNAL_TOKEN>`.
 * The backend is the client so that comms stays stateless with respect to
 * business data and can be restarted independently; on (re)connect the backend
 * asks for the session list and re-syncs.
 *
 * Ownership rule: comms reports OBSERVATIONS (robot_hello, robot_message,
 * robot_disconnected). It never decides mission/command state. The backend
 * decides what to send and whether a session is accepted.
 *
 * Scaling note: with N comms instances the backend needs a robot→instance
 * routing table (or a broker such as NATS/Redis Streams). The message shapes
 * below are broker-agnostic so that swap is a transport change only.
 */
import { z } from "zod";
import { HelloBody, PlatformToRobot, RobotToPlatform } from "./robot-link.js";
import { RegistrySnapshot } from "./registry.js";
export const SessionInfo = z.object({
    robotId: z.string().uuid(),
    sessionId: z.string().uuid(),
    connectedAt: z.number().int(),
    welcomed: z.boolean(),
    remoteAddr: z.string().default(""),
    rttMs: z.number().nullable().default(null),
});
// comms → backend
export const CommsToBackend = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("robot_hello"),
        robotId: z.string().uuid(),
        sessionId: z.string().uuid(),
        connectedAt: z.number().int(),
        remoteAddr: z.string().default(""),
        /**
         * `gen` claim of the link token the robot authenticated with. Lets the
         * backend reject sessions opened with a token issued before a credential
         * rotation (comms cannot know the current generation). Optional for
         * backwards compatibility; comms SHOULD always set it.
         */
        tokenGen: z.number().int().nonnegative().optional(),
        hello: HelloBody,
    }),
    z.object({
        type: z.literal("robot_message"),
        robotId: z.string().uuid(),
        sessionId: z.string().uuid(),
        receivedAt: z.number().int(),
        msg: RobotToPlatform,
    }),
    z.object({
        type: z.literal("robot_disconnected"),
        robotId: z.string().uuid(),
        sessionId: z.string().uuid(),
        at: z.number().int(),
        code: z.number().int(),
        reason: z.string(),
    }),
    z.object({
        type: z.literal("link_stats"),
        robotId: z.string().uuid(),
        sessionId: z.string().uuid(),
        rttMs: z.number().nonnegative(),
    }),
    z.object({
        type: z.literal("send_result"),
        requestId: z.string(),
        delivered: z.boolean(),
        reason: z.string().optional(),
    }),
    z.object({ type: z.literal("sessions"), sessions: z.array(SessionInfo) }),
]);
// backend → comms
export const BackendToComms = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("session_accept"),
        robotId: z.string().uuid(),
        sessionId: z.string().uuid(),
        registry: RegistrySnapshot,
    }),
    z.object({
        type: z.literal("session_reject"),
        robotId: z.string().uuid(),
        sessionId: z.string().uuid(),
        code: z.number().int(),
        reason: z.string(),
    }),
    z.object({
        type: z.literal("send"),
        /** Present when the backend needs a delivery receipt (commands). Omit for teleop. */
        requestId: z.string().optional(),
        robotId: z.string().uuid(),
        /** If set, only deliver on this session (prevents delivering to a newer, un-reconciled session). */
        sessionId: z.string().uuid().optional(),
        frame: PlatformToRobot,
    }),
    z.object({
        type: z.literal("disconnect"),
        robotId: z.string().uuid(),
        code: z.number().int(),
        reason: z.string(),
    }),
    z.object({ type: z.literal("list_sessions") }),
]);
/** Claims in a robot access token (HS256/EdDSA JWT issued by the backend, verified by comms). */
export const RobotTokenClaims = z.object({
    sub: z.string().uuid(), // robot id
    aud: z.literal("tcr-robot-link"),
    iss: z.literal("tcr-platform"),
    /** Credential generation; bumping it revokes outstanding tokens on next refresh. */
    gen: z.number().int().nonnegative(),
    exp: z.number().int(),
    iat: z.number().int(),
});
//# sourceMappingURL=internal.js.map